import React, { useState } from 'react';
import { ForecastChart } from './ForecastChart';
import { MoneySavedCounter } from './MoneySavedCounter';
import { DarkModeToggle } from './DarkModeToggle';
import { useDarkMode } from '../hooks/useDarkMode';
import { ChevronDown } from 'lucide-react';
export function BrochureLayout() {
  const {
    isDark,
    toggle
  } = useDarkMode();
  const currentUtcDate = new Date().toISOString().split('T')[0];
  // Substation configuration
  const substations = [{
    id: 'maria_elena',
    name: 'María Elena',
    displayName: 'Subestación María Elena',
    voltage: '',
    description: 'Comparación entre nuestra proyección y el pronóstico del Coordinador Eléctrico Nacional',
    colorTheme: {
      bg: 'bg-blue-50 dark:bg-blue-500/10',
      text: 'text-blue-700 dark:text-blue-400',
      border: 'border-blue-100 dark:border-blue-500/20',
      dot: 'bg-blue-500 dark:bg-blue-400',
      shadow: 'dark:shadow-blue-400/50'
    }
  }, {
    id: 'tamaya_110',
    name: 'Tamaya 110',
    displayName: 'Subestación Tamaya 110',
    voltage: '110kV',
    description: 'Análisis específico para la subestación Tamaya 110kV',
    colorTheme: {
      bg: 'bg-purple-50 dark:bg-purple-500/10',
      text: 'text-purple-700 dark:text-purple-400',
      border: 'border-purple-100 dark:border-purple-500/20',
      dot: 'bg-purple-500 dark:bg-purple-400',
      shadow: 'dark:shadow-purple-400/50'
    }
  }, {
    id: 'jadresic_220',
    name: 'Jadresic 220',
    displayName: 'Subestación Jadresic 220',
    voltage: '220kV',
    description: 'Análisis específico para la subestación Jadresic 220kV',
    colorTheme: {
      bg: 'bg-amber-50 dark:bg-amber-500/10',
      text: 'text-amber-700 dark:text-amber-400',
      border: 'border-amber-100 dark:border-amber-500/20',
      dot: 'bg-amber-500 dark:bg-amber-400',
      shadow: 'dark:shadow-amber-400/50'
    }
  }, {
    id: 'charrua_500',
    name: 'Charrúa 500',
    displayName: 'Subestación Charrúa 500',
    voltage: '500kV',
    description: 'Análisis específico para la subestación Charrúa 500kV',
    colorTheme: {
      bg: 'bg-emerald-50 dark:bg-emerald-500/10',
      text: 'text-emerald-700 dark:text-emerald-400',
      border: 'border-emerald-100 dark:border-emerald-500/20',
      dot: 'bg-emerald-500 dark:bg-emerald-400',
      shadow: 'dark:shadow-emerald-400/50'
    }
  }, {
    id: 'polpaico_500',
    name: 'Polpaico 500',
    displayName: 'Subestación Polpaico 500',
    voltage: '500kV',
    description: 'Análisis específico para la subestación Polpaico 500kV',
    colorTheme: {
      bg: 'bg-cyan-50 dark:bg-cyan-500/10',
      text: 'text-cyan-700 dark:text-cyan-400',
      border: 'border-cyan-100 dark:border-cyan-500/20',
      dot: 'bg-cyan-500 dark:bg-cyan-400',
      shadow: 'dark:shadow-cyan-400/50'
    }
  }];
  const [selectedSubstation, setSelectedSubstation] = useState(substations[0]);
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 transition-colors duration-500" data-id="element-22">
      {/* Subtle grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(to_right,#8882_1px,transparent_1px),linear-gradient(to_bottom,#8882_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)] pointer-events-none opacity-40" data-id="element-23" />

      <div className="relative" data-id="element-24">
        {/* Header */}
        <header className="border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md" data-id="element-25">
          <div className="max-w-7xl mx-auto px-6 py-6" data-id="element-26">
            <div className="flex items-center justify-between" data-id="element-27">
              <div className="flex items-center gap-3" data-id="element-28">
                <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-500 dark:from-blue-400 dark:to-purple-400 rounded-full shadow-lg dark:shadow-blue-500/30" data-id="element-29" />
                <div data-id="element-30">
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent" data-id="element-31">
                    VECTREI
                  </h1>
                  <p className="text-sm text-slate-600 dark:text-slate-400" data-id="element-32">
                    Proyección de CMg en tiempo real
                  </p>
                </div>
              </div>
              <DarkModeToggle isDark={isDark} onToggle={toggle} data-id="element-33" />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-6 py-12" data-id="element-34">
          <div data-id="element-35">
            <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6" data-id="element-36">
              <div className="flex-1" data-id="element-37">
                <div className="flex items-center gap-4 mb-3" data-id="element-38">
                  <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-50" data-id="element-39">
                    {selectedSubstation.displayName}
                  </h2>

                  {/* Substation Dropdown */}
                  <div className="relative" data-id="element-40">
                    <select value={selectedSubstation.id} onChange={e => {
                    const station = substations.find(s => s.id === e.target.value);
                    if (station) setSelectedSubstation(station);
                  }} className="appearance-none pl-4 pr-10 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 text-sm font-medium cursor-pointer hover:border-slate-300 dark:hover:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors" data-id="element-41">
                      {substations.map(station => <option key={station.id} value={station.id} data-id="element-42">
                          {station.name}
                        </option>)}
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-43" />
                  </div>
                </div>

                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium mb-3 border ${selectedSubstation.colorTheme.bg} ${selectedSubstation.colorTheme.text} ${selectedSubstation.colorTheme.border}`} data-id="element-44">
                  <div className={`w-1.5 h-1.5 rounded-full animate-pulse shadow-sm ${selectedSubstation.colorTheme.dot} ${selectedSubstation.colorTheme.shadow}`} data-id="element-45" />
                  Proyección 24 horas
                </div>

                <p className="text-slate-600 dark:text-slate-400 max-w-md leading-relaxed" data-id="element-46">
                  {selectedSubstation.description}
                </p>
              </div>

              <MoneySavedCounter chartId={selectedSubstation.id} title="Diferencia Promedio" key={selectedSubstation.id} data-id="element-47" />
            </div>

            <div className="rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-black/20 overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-id="element-48">
              <ForecastChart prediction_date={currentUtcDate} title="CMG (USD/MWH)" chartId={selectedSubstation.id} showRealData={true} substation={selectedSubstation.id} showMetrics={false} key={selectedSubstation.id} data-id="element-49" />
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="mt-24 border-t border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md" data-id="element-50">
          <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-51">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-slate-600 dark:text-slate-400" data-id="element-52">
              <div data-id="element-53">© 2025 VECTREI</div>
              <div data-id="element-54">Sistema de Proyección de Energía</div>
            </div>
          </div>
        </footer>
      </div>
    </div>;
}